// Decodes an audio blob into a peak overview (min/max per column) for drawing
// a static waveform image behind the marker editor's scrub track — a
// one-time decode, not the streaming HTMLAudioElement path MarkerEditorPlayer
// itself uses for actual playback, since this only needs the shape of the
// waveform, not a live audio graph.
//
// Peaks are stored at a fixed per-column duration (SECONDS_PER_COLUMN), not a
// fixed column count — so the same decoded array serves both the full-song
// overview (drawn at whatever width, downsampling further as needed) and the
// zoomed beat-alignment strip (which needs to pull out a precise ~2s slice
// and have it still look like real waveform detail, not a blurred average of
// the entire song).

import type { MetronomeSettings } from '../core/types';
import { secondsPerBeat } from '../core/markerFormat';

export interface WaveformPeaks {
  /** One [min, max] pair per column, values in [-1, 1]. */
  min: Float32Array;
  max: Float32Array;
  /** Duration, in seconds, that a single column of `min`/`max` represents. */
  secondsPerColumn: number;
}

const SECONDS_PER_COLUMN = 0.01; // 10ms/column — fine enough for a 2s zoomed view to show real detail.

/** Decodes `blob` into a peak overview at a fixed ~10ms-per-column
 * resolution, merging all channels down to one (mono overview is enough to
 * line up a click by eye — this isn't a stereo-correctness check, just a
 * visual reference). */
export async function decodeWaveformPeaks(blob: Blob): Promise<WaveformPeaks> {
  const arrayBuffer = await blob.arrayBuffer();
  const context = new OfflineAudioContext(1, 1, 44100);
  const audioBuffer = await context.decodeAudioData(arrayBuffer);

  const channelCount = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const sampleRate = audioBuffer.sampleRate;
  const samplesPerColumn = Math.max(1, Math.round(sampleRate * SECONDS_PER_COLUMN));
  const columns = Math.max(1, Math.ceil(length / samplesPerColumn));

  const min = new Float32Array(columns);
  const max = new Float32Array(columns);
  const channelData: Float32Array[] = [];
  for (let ch = 0; ch < channelCount; ch++) channelData.push(audioBuffer.getChannelData(ch));

  for (let col = 0; col < columns; col++) {
    const start = col * samplesPerColumn;
    const end = Math.min(length, start + samplesPerColumn);
    let colMin = 0;
    let colMax = 0;
    for (let ch = 0; ch < channelCount; ch++) {
      const data = channelData[ch];
      for (let i = start; i < end; i++) {
        const value = data[i];
        if (value < colMin) colMin = value;
        if (value > colMax) colMax = value;
      }
    }
    min[col] = colMin;
    max[col] = colMax;
  }

  return { min, max, secondsPerColumn: SECONDS_PER_COLUMN };
}

/** Draws the full peak overview onto `canvas`, sized to the canvas's own
 * current pixel dimensions (caller sets canvas.width/height to match its
 * rendered size × devicePixelRatio before calling this). Downsamples
 * `peaks` to fit however many columns the canvas is wide. */
export function drawWaveform(canvas: HTMLCanvasElement, peaks: WaveformPeaks, color: string): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const width = canvas.width;
  const height = canvas.height;
  ctx.clearRect(0, 0, width, height);

  const columns = peaks.min.length;
  const midY = height / 2;
  ctx.fillStyle = color;
  for (let x = 0; x < width; x++) {
    const col = Math.min(columns - 1, Math.floor((x / width) * columns));
    const yMax = midY - peaks.max[col] * midY;
    const yMin = midY - peaks.min[col] * midY;
    const barHeight = Math.max(1, yMin - yMax);
    ctx.fillRect(x, yMax, 1, barHeight);
  }
}

/** Draws just the slice of `peaks` covering [startSeconds, startSeconds +
 * windowSeconds), stretched to fill `canvas` — used by the zoomed
 * beat-alignment strip, which always shows a fixed-duration window rather
 * than the whole song. Columns outside the decoded range (window extends
 * before 0:00 or past the song's end) are simply left blank, same as the
 * silence that's actually there. */
export function drawWaveformWindow(
  canvas: HTMLCanvasElement,
  peaks: WaveformPeaks,
  startSeconds: number,
  windowSeconds: number,
  color: string,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const width = canvas.width;
  const height = canvas.height;
  ctx.clearRect(0, 0, width, height);

  const midY = height / 2;
  const totalColumns = peaks.min.length;
  ctx.fillStyle = color;
  for (let x = 0; x < width; x++) {
    const timeAtX = startSeconds + (x / width) * windowSeconds;
    const col = Math.floor(timeAtX / peaks.secondsPerColumn);
    if (col < 0 || col >= totalColumns) continue;
    const yMax = midY - peaks.max[col] * midY;
    const yMin = midY - peaks.min[col] * midY;
    const barHeight = Math.max(1, yMin - yMax);
    ctx.fillRect(x, yMax, 1, barHeight);
  }
}

export interface BeatTick {
  timeSeconds: number;
  /** 1-4, cycling, counted from the song's marked first beat — null when no
   * first beat has been marked yet (there's nothing to count FROM, so no
   * beat can be confidently called "1" of a measure). Assumes 4/4 as every
   * other beat-grid feature in this app already does. */
  beatNumber: number | null;
}

/** Snaps `timeSeconds` to the nearest point on the click-beat grid (anchored
 * at `offsetSeconds`, repeating every `60/bpm` seconds). Used to center the
 * beat-alignment strip's window on the nearest click BEAT to the playhead,
 * rather than the playhead's own raw time — so that nudging `offsetSeconds`
 * shifts which absolute time is "nearest" and therefore pans the whole
 * window (waveform + ticks together), instead of only moving the ticks
 * against a waveform slice frozen at the untouched playhead position. */
export function nearestBeatTime(timeSeconds: number, bpm: number, offsetSeconds: number): number {
  const secondsPerBeat = 60 / bpm;
  const beatsFromOffset = Math.round((timeSeconds - offsetSeconds) / secondsPerBeat);
  return offsetSeconds + beatsFromOffset * secondsPerBeat;
}

/** The next beat one whole step in `direction` (+1 or -1) from whichever
 * beat `timeSeconds` is currently nearest to — used by the marker-placement
 * "Snap to beat" nudge buttons. `timeSeconds` is expected to already sit
 * (approximately) on a beat, since the previous nudge put it there, but
 * HTMLMediaElement.currentTime doesn't guarantee returning exactly the
 * value assigned to it — it can quantize to the nearest sample/frame,
 * landing a hair before or after the true beat time. Rounding to the
 * nearest beat FIRST (rather than flooring/ceiling the raw value) absorbs
 * that drift in either direction before stepping, so repeated clicks in the
 * same direction always advance by a full beat instead of occasionally
 * landing back on the same beat and appearing stuck. */
export function adjacentBeatTime(timeSeconds: number, bpm: number, offsetSeconds: number, direction: 1 | -1): number {
  const secondsPerBeat = 60 / bpm;
  const beatsFromOffset = Math.round((timeSeconds - offsetSeconds) / secondsPerBeat);
  return offsetSeconds + (beatsFromOffset + direction) * secondsPerBeat;
}

/** Which whole click-grid beat `timeSeconds` currently falls on (0, 1, 2...,
 * or negative before `offsetSeconds`) — a monotonically-increasing integer
 * that changes exactly once per beat boundary crossed, unlike
 * `nearestBeatTime` (which snaps to whichever beat is CLOSEST and can jump
 * back and forth near the midpoint between two beats). Used to drive a live
 * visual beat flash from playhead position alone: comparing this value
 * frame-to-frame tells the caller exactly when a new beat has started,
 * without needing any real-time event from the audio engine itself (the
 * synthetic click's own scheduler runs ahead of audible time and never
 * emits beat events at all — see SyntheticClickLayer's doc comment). */
export function currentBeatOrdinal(timeSeconds: number, bpm: number, offsetSeconds: number): number {
  const secondsPerBeat = 60 / bpm;
  return Math.floor((timeSeconds - offsetSeconds) / secondsPerBeat);
}

/** Like `currentBeatOrdinal`, but at sub-beat resolution — for driving a
 * subdivision tick's own pulse from playhead position, the same way
 * currentBeatOrdinal drives the main beat. `subdivisionCount` is the
 * current song/metronome's MetronomeSettings.subdivision (1, 2, or 4 — how
 * many ticks-per-beat, including the beat itself as tick 0). The returned
 * `beatOrdinal` is which MAIN beat this slot belongs to (i.e. the beat it
 * comes at-or-after — a main beat's own slot and every subdivision that
 * follows it share the same beatOrdinal), and `subdivisionIndex` is 0 for
 * the main beat itself, 1..subdivisionCount-1 for each tick after it — the
 * exact same 0-based convention BeatEvent.subdivisionIndex already uses on
 * the live-metronome path, so both drivers of the shared beatVisual module
 * (and pulseTierFor, which maps this index to a pulse size) speak the same
 * "which slot" language regardless of which one is actually firing. */
export function currentSubdivisionSlot(
  timeSeconds: number,
  bpm: number,
  offsetSeconds: number,
  subdivisionCount: number,
): { beatOrdinal: number; subdivisionIndex: number } {
  const secondsPerBeat = 60 / bpm;
  const tickOrdinal = Math.floor((timeSeconds - offsetSeconds) / (secondsPerBeat / subdivisionCount));
  const subdivisionIndex = ((tickOrdinal % subdivisionCount) + subdivisionCount) % subdivisionCount;
  return {
    beatOrdinal: Math.floor(tickOrdinal / subdivisionCount),
    subdivisionIndex,
  };
}

/** Maps a beat ordinal (see `currentBeatOrdinal`) to a 1-4 display position
 * within a measure, anchored so `beatOneReferenceSeconds` lands on 1 — same
 * "assume 4/4, only meaningful once a reference beat is marked" convention
 * `computeBeatTicks` already uses for the beat-alignment strip's numbered
 * ticks. Returns null if no reference has been marked yet (nothing to
 * anchor "1" to), matching computeBeatTicks's own null-when-unmarked case. */
export function beatPositionInMeasure(
  beatOrdinal: number,
  bpm: number,
  offsetSeconds: number,
  beatOneReferenceSeconds: number | null,
): number | null {
  if (beatOneReferenceSeconds === null) return null;
  const referenceOrdinal = currentBeatOrdinal(beatOneReferenceSeconds, bpm, offsetSeconds);
  const beatsFromReference = beatOrdinal - referenceOrdinal;
  return ((beatsFromReference % 4) + 4) % 4 + 1;
}

/** How many click beats fall between the click grid's own beat 0
 * (`offsetSeconds`) and the song's marked real first beat
 * (`firstBeatTimeSeconds`) — this is the pre-roll's pickup-beat count,
 * DERIVED from the marked timestamp rather than typed in by hand, so it
 * self-corrects if BPM/offset are retuned later instead of going stale
 * against a hand-entered number. Clamped to 0: the marked first beat is
 * expected to land at or after the click's own beat 0, but floating-point
 * nudging could in principle land it a hair before, and a negative pickup
 * count wouldn't mean anything. Returns 0 when there's nothing marked yet.
 * Takes the full metronome settings (not just bpm) and goes through the
 * shared secondsPerBeat helper — SyntheticClickLayer's real click grid is
 * spaced by secondsPerBeat (which halves for an eighth-note subdivision),
 * so computing pickup beats from a bare 60/bpm here would silently drift
 * from the actual grid on any song using eighth notes, throwing off the
 * count-in's own beat count by roughly 2x. */
export function pickupBeatsFromFirstBeatTime(
  firstBeatTimeSeconds: number | null,
  metronomeSettings: Pick<MetronomeSettings, 'bpm' | 'beatUnit'>,
  offsetSeconds: number,
): number {
  if (firstBeatTimeSeconds === null) return 0;
  return Math.max(0, Math.round((firstBeatTimeSeconds - offsetSeconds) / secondsPerBeat(metronomeSettings)));
}

/** Computes click-beat timestamps across [startSeconds, startSeconds +
 * windowSeconds), using the exact same anchor math SyntheticClickLayer uses
 * to schedule real click audio: beat 0 lands at `offsetSeconds` (may be
 * negative/land before 0:00), beats repeat every `secondsPerBeat` from
 * there. The synthetic click itself never accents any beat over another
 * (every click sounds the same), so there is no audible way to say which
 * beat is "1" of a measure UNLESS the user has explicitly designated one —
 * `beatOneReferenceSeconds` is whichever beat they've confirmed via "Mark
 * as Aligned" (NOT necessarily the song's true first beat — any beat lined
 * up with the click works), only then can beats be confidently counted
 * 1-2-3-4 (assumes 4/4). Pass `null` to get plain ticks with no numbering,
 * e.g. before anything's been marked yet. Only used to draw visual
 * reference ticks — never touches actual playback scheduling. */
export function computeBeatTicks(
  startSeconds: number,
  windowSeconds: number,
  bpm: number,
  offsetSeconds: number,
  beatOneReferenceSeconds: number | null = null,
): BeatTick[] {
  const secondsPerBeat = 60 / bpm;
  const endSeconds = startSeconds + windowSeconds;

  const firstBeatIndex = Math.floor((startSeconds - offsetSeconds) / secondsPerBeat) - 1;
  const ticks: BeatTick[] = [];
  for (let beatIndex = firstBeatIndex; ; beatIndex++) {
    const timeSeconds = offsetSeconds + beatIndex * secondsPerBeat;
    if (timeSeconds > endSeconds) break;
    if (timeSeconds >= startSeconds) {
      let beatNumber: number | null = null;
      if (beatOneReferenceSeconds !== null) {
        const beatsFromReference = Math.round((timeSeconds - beatOneReferenceSeconds) / secondsPerBeat);
        beatNumber = ((beatsFromReference % 4) + 4) % 4 + 1;
      }
      ticks.push({ timeSeconds, beatNumber });
    }
  }
  return ticks;
}

/** Draws vertical beat-tick lines, per-beat numbers (once a first beat has
 * been marked), and a fixed center playhead line (with small arrow carets
 * top/bottom, so it reads as "a fixed reference point" rather than an
 * arbitrary colored line a new user has no way to interpret) onto `canvas`,
 * on top of whatever waveform is already drawn there. Every beat TICK is
 * drawn identically regardless of its number — there's no accented/taller
 * downbeat tick, since the synthetic click can't distinguish one beat from
 * another either; the number is text, not a height/color difference. */
export function drawBeatTicks(
  canvas: HTMLCanvasElement,
  ticks: BeatTick[],
  startSeconds: number,
  windowSeconds: number,
  colors: { beat: string; playhead: string },
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const width = canvas.width;
  const height = canvas.height;

  ctx.fillStyle = colors.beat;
  const fontSize = Math.max(9, Math.round(height * 0.14));
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textBaseline = 'bottom';
  for (const tick of ticks) {
    const x = ((tick.timeSeconds - startSeconds) / windowSeconds) * width;
    ctx.fillRect(x - 1, 0, 2, height);
    if (tick.beatNumber !== null) {
      ctx.fillText(String(tick.beatNumber), x + 3, height - 2);
    }
  }

  const centerX = width / 2;
  ctx.fillStyle = colors.playhead;
  ctx.fillRect(centerX - 1, 0, 2, height);
  const caretSize = Math.max(4, Math.round(height * 0.08));
  ctx.beginPath();
  ctx.moveTo(centerX - caretSize, 0);
  ctx.lineTo(centerX + caretSize, 0);
  ctx.lineTo(centerX, caretSize);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(centerX - caretSize, height);
  ctx.lineTo(centerX + caretSize, height);
  ctx.lineTo(centerX, height - caretSize);
  ctx.closePath();
  ctx.fill();
}
